import { useFileInfo } from "@/api/services/global/file";
import { IconClose, IconDownloadBlue, IconFile, IconImage, IconSearchBlue } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { FILE_EXISTED, MIME_TYPES } from "@/constants/file";
import { EMPTY_STR } from "@/constants/string";
import { useDialog } from "@/hooks/useDialog";
import { button, primary, text } from "@/theme/colors";
import { textEllipsis } from "@/theme/minxin";
import { typography } from "@/theme/typography";
import { TRequiredOne } from "@/types/common";
import { TExtendFile } from "@/types/file";
import { showError } from "@/utils/error";
import { downloadFileByUrl, downloadFileFromPath } from "@/utils/export";
import { convertByteToKb, convertByteToMb } from "@/utils/file";
import { Box, Skeleton, Stack, StackProps, Typography, css, styled } from "@mui/material";
import { FC, useState } from "react";
import { ImagePreviewModal } from "@/features/company/components/company-detail/ImagePreviewModal";

type TCompanyFileItemProps = {
  onRemove?: () => void;
  currentFileIndex?: number;
  errorMsg?: string;
  downloadable?: boolean;
  previewEnabled?: boolean;
  hideIcon?: boolean;
  enableFileSize?: boolean;
  imagePaths: string[];
  files?: TExtendFile[];
} & TRequiredOne<{
  file: File;
  filePath?: string;
}> &
  StackProps;

export const CompanyFileItem: FC<TCompanyFileItemProps> = ({
  currentFileIndex,
  files,
  file,
  filePath,
  downloadable,
  enableFileSize,
  previewEnabled,
  imagePaths,
  ...rest
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadFile = async () => {
    if (!downloadable) return;
    try {
      setIsDownloading(true);
      let url = "";
      if (file) {
        url = URL.createObjectURL(file);
        await downloadFileByUrl(file?.name, url);
      }
      if (!file && filePath) {
        await downloadFileFromPath(filePath);
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsDownloading(false);
    }
  };
  const { data: infoResult } = useFileInfo(filePath);
  const infoData = infoResult?.data;

  if (file && infoData) {
    return (
      <CompanyFileItemContent
        currentFileIndex={currentFileIndex}
        filePath={filePath}
        files={files}
        fileName={file.name}
        fileSize={file.size}
        mimeType={infoData.mimeType}
        downloadable={downloadable}
        enableFileSize={enableFileSize}
        hideIcon
        previewEnabled={previewEnabled}
        isDownloading={isDownloading}
        onDownload={handleDownloadFile}
        imagePaths={imagePaths}
        {...rest}
      />
    );
  }
  if (filePath) {
    return (
      <FilePathItem
        currentFileIndex={currentFileIndex}
        files={files}
        filePath={filePath}
        previewEnabled={previewEnabled}
        isDownloading={isDownloading}
        downloadable={downloadable}
        imagePaths={imagePaths}
        onDownload={handleDownloadFile}
        {...rest}
      />
    );
  }
  return null;
};

type TFilePathItemProps = {
  currentFileIndex?: number;
  files?: TExtendFile[];
  filePath: string;
  previewEnabled?: boolean;
  onDownload: () => void;
  isDownloading: boolean;
  imagePaths?: string[];
} & TCompanyFileItemProps;

const FilePathItem: FC<TFilePathItemProps> = ({ currentFileIndex, imagePaths, files, filePath, previewEnabled, onDownload, ...rest }) => {
  if (!filePath || filePath === FILE_EXISTED) return null;
  const { data: infoResult, isLoading } = useFileInfo(filePath);
  const infoData = infoResult?.data;

  if (!infoData && !isLoading) return null;
  return (
    <CompanyFileItemContent
      currentFileIndex={currentFileIndex}
      filePath={filePath}
      files={files}
      isLoading={isLoading}
      fileName={infoData?.name || EMPTY_STR.TEXT}
      fileSize={infoData?.size || 0}
      mimeType={infoData?.mimeType || EMPTY_STR.TEXT}
      previewEnabled={previewEnabled}
      enableFileSize={false}
      imagePaths={imagePaths}
      hideIcon
      onDownload={onDownload}
      {...rest}
    />
  );
};

type TCompanyFileItemContentProps = {
  onRemove?: () => void;
  onDownload: () => void;
  errorMsg?: string;
  downloadable?: boolean;
  previewEnabled?: boolean;
  currentFileIndex?: number;
  fileName: string;
  fileSize: number;
  mimeType: string;
  isLoading?: boolean;
  isDownloading?: boolean;
  hideIcon?: boolean;
  enableFileSize?: boolean;
  filePath?: string;
  imagePaths: string[];
  files?: TExtendFile[];
} & StackProps;

const CompanyFileItemContent: FC<TCompanyFileItemContentProps> = ({
  onRemove,
  onDownload,
  errorMsg,
  downloadable = false,
  previewEnabled = false,
  isLoading = false,
  isDownloading = false,
  currentFileIndex = 0,
  fileName,
  fileSize,
  mimeType,
  hideIcon = false,
  enableFileSize = false,
  filePath,
  imagePaths,
  files = [],
  ...rest
}) => {
  const imagePreview = useDialog();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % files.length);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + files.length) % files.length);
  };

  return (
    <>
      <Stack {...rest} gap={0.5}>
        <Stack direction="row" alignItems="center">
          <CompanyFileItemWrap error={!!errorMsg} isDownloading={isDownloading}>
            {!hideIcon && !isLoading && <>{MIME_TYPES.IMAGE.includes(mimeType) ? <IconImage fontSize="32px" /> : <IconFile fontSize="32px" />}</>}
            {enableFileSize ? (
              <>
                <FileName>{isLoading ? <Skeleton variant="text" height={32} /> : fileName}</FileName> │
                <MimeType>{isLoading ? <Skeleton variant="text" height={32} /> : `${convertByteToKb(fileSize).toFixed(2)}KB`}</MimeType>)
              </>
            ) : (
              <>
                <FileNameNotEllipsis>{isLoading ? <Skeleton variant="text" height={32} /> : fileName}</FileNameNotEllipsis>
              </>
            )}
          </CompanyFileItemWrap>
          {!enableFileSize || isLoading ? null : (
            <Typography variant="body14" mr={0.5} whiteSpace="nowrap">
              {convertByteToMb(fileSize).toFixed(2)}MB
            </Typography>
          )}
          {onRemove && (
            <IconBox onClick={onRemove}>
              <IconClose />
            </IconBox>
          )}
          {previewEnabled && (
            <IconBox sx={{ width: 20, height: 20, padding: 0 }} onClick={() => imagePreview.show()}>
              <IconSearchBlue width={20} height={20} color={primary[500]} />
            </IconBox>
          )}
          {downloadable && (
            <IconBox
              sx={{
                width: 20,
                height: 20,
                padding: 0,
                marginLeft: 0.5,
                cursor: isDownloading ? "not-allowed" : "pointer",
                opacity: isDownloading ? 0.5 : 1,
              }}
              onClick={isDownloading ? undefined : onDownload}
              disabled={isDownloading}
            >
              <IconDownloadBlue color={isDownloading ? button.disable : primary[500]} width={20} height={20} />
            </IconBox>
          )}
        </Stack>
        {errorMsg && (
          <Typography variant="body14" color={text.error} whiteSpace="pre-line">
            {errorMsg}
          </Typography>
        )}
      </Stack>
      {previewEnabled && (
        <ImagePreviewModal
          currentFileIndex={currentFileIndex}
          filePath={filePath}
          imagePaths={imagePaths}
          open={imagePreview.open}
          onClose={imagePreview.cancel}
          onNext={handleNextImage}
          onPrev={handlePrevImage}
        />
      )}
    </>
  );
};

const options = { shouldForwardProp: (propName: string) => !["error", "isDownloading"].includes(propName) };

const CompanyFileItemWrap = styled(Box, options)<{ error: boolean; isDownloading: boolean }>`
  display: flex;
  align-items: center;
  //flex: 1;
  gap: 16px;
  padding-bottom: 8px;
  ${({ error }) => error && CompanyFileItemError}
  ${({ isDownloading }) => isDownloading && CompanyFileItemDownloading}
`;

const CompanyFileItemDownloading = css`
  pointer-events: none;
  opacity: 0.8;
`;

const CompanyFileItemError = css`
  &,
  button {
    color: ${text.error};
  }
`;

const FileName = styled(Typography)`
  max-width: 86px;
  ${textEllipsis};
  ${typography.body14};
  flex: 1;
`;

const FileNameNotEllipsis = styled(Typography)`
  max-width: 400px;
  ${typography.body14};
  ${textEllipsis};
  margin-right: 24px;
  margin-top: 8px;
`;

const MimeType = styled(Typography)`
  max-width: 64px;
  ${typography.body14};
  flex: 1;
`;
